import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboardIcon, PackageIcon, ShoppingCartIcon, TagIcon, BanknoteIcon, SettingsIcon, ChevronDownIcon, BellIcon, SearchIcon, SunIcon, MoonIcon, LogOutIcon, ChevronRightIcon, UserIcon } from 'lucide-react';
import { UserProfilePopup } from './UserProfilePopup';
import { useAuth } from '../../contexts/AuthContext';
export const AdminDashboardLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    products: true,
    settings: false
  });
  const location = useLocation();
  const {
    logout
  } = useAuth();
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    // In a real app, we would apply dark mode to the entire app here
  };
  const toggleProfilePopup = () => {
    setIsProfileOpen(!isProfileOpen);
  };
  const toggleMenu = (menu: string) => {
    setExpandedMenus({
      ...expandedMenus,
      [menu]: !expandedMenus[menu]
    });
  };
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  const isSubpathActive = (path: string) => {
    return location.pathname.startsWith(path);
  };
  const handleLogout = () => {
    logout();
  };
  const menuItems = [{
    title: 'Dashboard',
    icon: <LayoutDashboardIcon className="h-5 w-5" data-id="element-53" />,
    path: '/admin',
    hasSubmenu: false
  }, {
    title: 'Products',
    icon: <PackageIcon className="h-5 w-5" data-id="element-54" />,
    path: '#',
    hasSubmenu: true,
    submenuKey: 'products',
    submenu: [{
      title: 'SPU Management',
      path: '/admin/products/spu'
    }, {
      title: 'Collections',
      path: '/admin/products/collections'
    }, {
      title: 'Brands',
      path: '/admin/products/brands'
    }, {
      title: 'Categories',
      path: '/admin/products/categories'
    }]
  }, {
    title: 'Orders',
    icon: <ShoppingCartIcon className="h-5 w-5" data-id="element-55" />,
    path: '/admin/orders',
    hasSubmenu: false
  }, {
    title: 'Coupons',
    icon: <TagIcon className="h-5 w-5" data-id="element-56" />,
    path: '/admin/coupons',
    hasSubmenu: false
  }, {
    title: 'Settlements',
    icon: <BanknoteIcon className="h-5 w-5" data-id="element-57" />,
    path: '/admin/settlements',
    hasSubmenu: false
  }, {
    title: 'Settings',
    icon: <SettingsIcon className="h-5 w-5" data-id="element-58" />,
    path: '/admin/settings',
    hasSubmenu: false
  }];
  return <div className={`flex h-screen bg-gray-50 ${isDarkMode ? 'dark' : ''}`} data-id="element-59">
      {/* Sidebar */}
      <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} transition-width duration-300 bg-white border-r border-gray-200 flex flex-col`} data-id="element-60">
        {/* Logo */}
        <div className="h-16 flex items-center justify-center border-b border-gray-200" data-id="element-61">
          <Link to="/admin" className="flex items-center" data-id="element-62">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tpKLaYzfGrRLG8howg5M89/sellup-logo.png" alt="SellUp Admin" className="h-8" data-id="element-63" />
            {isSidebarOpen && <span className="ml-2 text-xl font-semibold text-gray-900" data-id="element-64">
                Admin
              </span>}
          </Link>
        </div>
        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto" data-id="element-65">
          <ul className="px-2 space-y-1" data-id="element-66">
            {menuItems.map(item => <li key={item.title} data-id="element-67">
                {item.hasSubmenu ? <div data-id="element-68">
                    <button onClick={() => toggleMenu(item.submenuKey)} className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${expandedMenus[item.submenuKey] || isSubpathActive(item.title.toLowerCase()) ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-69">
                      <div className="flex items-center" data-id="element-70">
                        {item.icon}
                        {isSidebarOpen && <span className="ml-3" data-id="element-71">{item.title}</span>}
                      </div>
                      {isSidebarOpen && <ChevronDownIcon className={`h-4 w-4 transition-transform ${expandedMenus[item.submenuKey] ? 'transform rotate-180' : ''}`} data-id="element-72" />}
                    </button>
                    {expandedMenus[item.submenuKey] && isSidebarOpen && <ul className="ml-6 mt-1 space-y-1" data-id="element-73">
                        {item.submenu.map(subitem => <li key={subitem.title} data-id="element-74">
                            <Link to={subitem.path} className={`block px-3 py-2 text-sm rounded-md ${isActive(subitem.path) || location.pathname === subitem.path ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-75">
                              {subitem.title}
                            </Link>
                          </li>)}
                      </ul>}
                  </div> : <Link to={item.path} className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive(item.path) || isSubpathActive(item.path) ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-76">
                    {item.icon}
                    {isSidebarOpen && <span className="ml-3" data-id="element-77">{item.title}</span>}
                  </Link>}
              </li>)}
          </ul>
        </nav>
        {/* Bottom actions */}
        <div className="p-4 border-t border-gray-200" data-id="element-78">
          <button onClick={toggleSidebar} className="w-full flex items-center justify-center p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md" data-id="element-79">
            <ChevronRightIcon className={`h-5 w-5 transition-transform ${isSidebarOpen ? '' : 'transform rotate-180'}`} data-id="element-80" />
            {isSidebarOpen && <span className="ml-2" data-id="element-81">Collapse</span>}
          </button>
        </div>
      </aside>
      {/* Main Content */}
      <div className="flex flex-col flex-1 overflow-hidden" data-id="element-82">
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6" data-id="element-83">
          <div className="flex items-center" data-id="element-84">
            <div className="relative w-64" data-id="element-85">
              <input type="text" placeholder="Search..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-86" />
              <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-87" />
            </div>
          </div>
          <div className="flex items-center space-x-4" data-id="element-88">
            <button onClick={toggleDarkMode} className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md" data-id="element-89">
              {isDarkMode ? <SunIcon className="h-5 w-5" data-id="element-90" /> : <MoonIcon className="h-5 w-5" data-id="element-91" />}
            </button>
            <div className="relative" data-id="element-92">
              <button className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md" data-id="element-93">
                <BellIcon className="h-5 w-5" data-id="element-94" />
                <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full" data-id="element-95"></span>
              </button>
            </div>
            <div className="relative" data-id="element-96">
              <button onClick={toggleProfilePopup} className="flex items-center space-x-2 text-gray-700 hover:text-gray-900" data-id="element-97">
                <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center text-white" data-id="element-98">
                  <UserIcon className="h-5 w-5" data-id="element-99" />
                </div>
                <span className="font-medium" data-id="element-100">Admin User</span>
              </button>
              {isProfileOpen && <UserProfilePopup onClose={toggleProfilePopup} data-id="element-101" />}
            </div>
            <button onClick={handleLogout} className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md" data-id="element-102">
              <LogOutIcon className="h-5 w-5" data-id="element-103" />
            </button>
          </div>
        </header>
        {/* Main Content Area */}
        <main className="flex-1 overflow-auto p-6 bg-gray-50" data-id="element-104">
          <Outlet data-id="element-105" />
        </main>
      </div>
    </div>;
};